/**
 * Talking to the send-drop edge function.
 *
 * WHY THIS IS ITS OWN FILE
 * ------------------------
 * Two things here were learned the expensive way and must not be re-derived at
 * each call site.
 *
 * 1. supabase-js DISCARDS THE BODY OF ANY NON-2xx RESPONSE.
 *    It reports the whole thing as "Failed to send a request to the Edge
 *    Function", which reads exactly like a network or CORS failure. It is not:
 *    the function was reached and answered, and its answer says what is wrong.
 *    That misreading previously cost two unnecessary redeploys chasing a CORS
 *    problem that never existed. The body is recoverable from `error.context`,
 *    which is the Response object, and unwrapping it here means no caller can
 *    make that mistake again.
 *
 * 2. THE BATCH LOOP CAN SPIN FOREVER IF ITS CONDITION IS `remaining > 0`.
 *    Sending is batched because Stannp is one HTTP call per postcard and a
 *    285-door cell outlives an edge function's wall clock. But:
 *      - a TEST records nothing, so `remaining` never falls;
 *      - a FAILED piece stays `planned`, so `remaining` never falls either.
 *    Either case would retry the same batch endlessly against a paid API. The
 *    loop therefore continues on PROGRESS, not on work outstanding.
 */

export type Vendor = "pcm" | "stannp";

export type SendRequest = {
  vendor: Vendor;
  mode: "test" | "live";
  confirm?: boolean;
  cell?: string;
  touch?: string;
  mailing_ids?: number[];
  limit?: number;
};

export type SendResponse = {
  ok?: boolean;
  mode?: string;
  vendor?: string;
  order_ref?: string;
  creative_status?: string;
  attempted?: number;
  accepted?: number;
  /** Rows the database confirms it wrote. Compared against `accepted`. */
  recorded?: number;
  record_error?: string | null;
  remaining?: number;
  failures?: { ref: string; error: string }[];
  proofs?: string[];
  note?: string;
  /** Present when the function refused. */
  error?: string;
  detail?: string;
  problems?: string[];
  fix?: string;
  status?: number;
};

/** One call. Never throws for a refusal -- a refusal is an answer. */
export async function callSendDrop(req: SendRequest): Promise<SendResponse> {
  const { getSupabaseClient } = await import("../providers/supabase/supabase");
  const { data, error } = await getSupabaseClient().functions.invoke(
    "send-drop",
    {
      body: req,
    },
  );

  if (!error) return (data ?? {}) as SendResponse;

  // See note 1 above: the real answer is inside error.context.
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body = (await ctx.json()) as SendResponse;
      return { ...body, status: ctx.status };
    } catch {
      /* fall through to the generic message below */
    }
  }
  return { error: error.message || "the send function could not be reached" };
}

export type Progress = {
  accepted: number;
  recorded: number;
  remaining: number;
  batches: number;
};

/**
 * Send until there is nothing left, or until a batch stops making progress.
 *
 * `onProgress` fires after every batch so the UI can show movement rather than
 * freezing for minutes. The return value is the LAST response plus the running
 * totals; the caller compares `accepted` against `recorded` itself, because a
 * shortfall there means the printer took cards the ledger does not know about
 * and that must never be summarized away.
 */
export async function sendBatches(
  req: SendRequest,
  onProgress: (p: Progress, last: SendResponse) => void,
  maxBatches = 40,
  // Injected so the loop can be tested without a network, a credential or a
  // printer. The guards below are the reason: each one exists to prevent an
  // unbounded retry against an API that charges per call, and a rule that
  // expensive should be provable without spending anything to prove it.
  call: (r: SendRequest) => Promise<SendResponse> = callSendDrop,
): Promise<{ last: SendResponse; totals: Progress }> {
  const totals: Progress = {
    accepted: 0,
    recorded: 0,
    remaining: 0,
    batches: 0,
  };
  let last: SendResponse = {};

  for (let i = 0; i < maxBatches; i++) {
    last = await call(req);
    totals.batches += 1;
    totals.accepted += last.accepted ?? 0;
    totals.recorded += last.recorded ?? 0;
    totals.remaining = last.remaining ?? 0;
    onProgress({ ...totals }, last);

    if (last.error) break;
    // A rehearsal is always exactly one batch: it records nothing, so there is
    // no such thing as "the next batch" -- the queue is unchanged.
    if (req.mode === "test") break;
    // No progress means the next attempt is the same attempt. Stop and let the
    // failures be read rather than retrying a systematic rejection at cost.
    if (!last.accepted) break;
    if (!last.remaining) break;
  }
  return { last, totals };
}
